import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Send,
  Plus,
  Users,
  User,
  Globe,
  Image,
  Link as LinkIcon,
  Loader2,
  List,
  Bell,
  Edit,
  Trash2,
  Eye,
  Calendar,
  MoreVertical
} from 'lucide-react';
import { Card, Button, Modal, Alert, Badge, LoadingSpinner, ConfirmDialog } from '../../components/common';
import { useForm } from 'react-hook-form';
import {
  useCreateNotification,
  useNotificationStats,
  useAdminNotifications,
  useDeleteAdminNotification,
  useUpdateNotification
} from '../../hooks';

import QuickActions from '@/components/admin/QuickActions';

interface NotificationFormData {
  title: string;
  body: string;
  imageUrl?: string;
  actionButtonText?: string;
  actionButtonUrl?: string;
  recipientType: 'all' | 'group' | 'users';
  groupId?: string;
  userCedulas?: string;
}

type TabType = 'create' | 'history';

const AdminNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const createNotificationMutation = useCreateNotification();

  // Estados para historial
  const [page, setPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  // Queries
  const { data: statsData, isLoading: statsLoading } = useNotificationStats();
  const { data: notificationsData, isLoading: notificationsLoading } = useAdminNotifications({
    page,
    limit: 10
  });

  const deleteNotificationMutation = useDeleteAdminNotification();
  const updateNotificationMutation = useUpdateNotification();

  const stats = statsData?.stats || {
    sentToday: 0,
    usersReached: 0,
    deliveryRate: 0,
    openRate: 0
  };

  const notifications = notificationsData?.notifications || [];
  const pagination = notificationsData?.pagination;

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: errorsCreate },
    reset: resetCreate,
    watch: watchCreate,
  } = useForm<NotificationFormData>({
    defaultValues: { recipientType: 'all' }
  });

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: errorsEdit },
    reset: resetEdit,
  } = useForm<NotificationFormData>();

  const recipientType = watchCreate('recipientType');

  const onSendNotification = async (data: NotificationFormData) => {
    try {
      const notificationPayload = {
        title: data.title,
        body: data.body,
        imageUrl: data.imageUrl || undefined,
        actionButtonText: data.actionButtonText || undefined,
        actionButtonUrl: data.actionButtonUrl || undefined,
        recipients: {
          type: data.recipientType,
          ...(data.recipientType === 'group' && data.groupId && { groupId: data.groupId }),
          ...(data.recipientType === 'users' && data.userCedulas && {
            userCedulas: data.userCedulas.split(',').map(c => c.trim()).filter(Boolean)
          })
        }
      };

      await createNotificationMutation.mutateAsync(notificationPayload);
      setShowCreateModal(false);
      resetCreate();
    } catch (error) {
      console.error('Error enviando notificación:', error);
    }
  };

  const handleEdit = (notification: any) => {
    setSelectedNotification(notification);
    resetEdit({
      title: notification.title,
      body: notification.body,
      imageUrl: notification.imageUrl || '',
      actionButtonText: notification.actionButtonText || '',
      actionButtonUrl: notification.actionButtonUrl || ''
    });
    setShowEditModal(true);
    setActionMenuOpen(null);
  };

  const handleDelete = (notification: any) => {
    setSelectedNotification(notification);
    setShowDeleteDialog(true);
    setActionMenuOpen(null);
  };

  const handleView = (notification: any) => {
    setSelectedNotification(notification);
    setShowViewModal(true);
    setActionMenuOpen(null);
  };

  const confirmDelete = async () => {
    if (!selectedNotification) return;
    try {
      await deleteNotificationMutation.mutateAsync(selectedNotification.id);
      setShowDeleteDialog(false);
      setSelectedNotification(null);
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  const onUpdateNotification = async (data: NotificationFormData) => {
    if (!selectedNotification) return;
    try {
      await updateNotificationMutation.mutateAsync({
        id: selectedNotification.id,
        data: {
          title: data.title,
          body: data.body,
          imageUrl: data.imageUrl || undefined,
          actionButtonText: data.actionButtonText || undefined,
          actionButtonUrl: data.actionButtonUrl || undefined
        }
      });
      setShowEditModal(false);
      setSelectedNotification(null);
      resetEdit();
    } catch (error) {
      console.error('Error actualizando notificación:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brinsa-azul mb-2">
              Sistema de Notificaciones
            </h1>
            <p className="text-lg text-brinsa-azulOscuro/80">
              Envía mensajes y gestiona notificaciones
            </p>
          </div>

          {activeTab === 'create' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Nueva Notificación
            </Button>
          )}
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.05 }}
      >
        <Card>
          <div className="border-b border-brinsa-celeste/30">
            <div className="flex space-x-1 p-2">
              <button
                onClick={() => setActiveTab('create')}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${activeTab === 'create'
                    ? 'bg-brinsa-azul text-white'
                    : 'text-brinsa-azul hover:bg-brinsa-celeste/10'
                  }`}
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar Notificación
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${activeTab === 'history'
                    ? 'bg-brinsa-azul text-white'
                    : 'text-brinsa-azul hover:bg-brinsa-celeste/10'
                  }`}
              >
                <List className="w-4 h-4 mr-2" />
                Historial
              </button>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Contenido según tab */}
      {activeTab === 'create' ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          <Card className="p-6 text-center border border-brinsa-celeste/30 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Send className="w-6 h-6 text-brinsa-azul" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {statsLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : stats.sentToday}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Enviadas hoy</div>
          </Card>

          <Card className="p-6 text-center border border-brinsa-celeste/30 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-brinsa-azul" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {statsLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : stats.usersReached}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Usuarios alcanzados</div>
          </Card>

          <Card className="p-6 text-center border border-brinsa-celeste/30 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Globe className="w-6 h-6 text-brinsa-azul" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {statsLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : `${stats.deliveryRate}%`}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Tasa de entrega</div>
          </Card>

          <Card className="p-6 text-center border border-brinsa-celeste/30 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-lg flex items-center justify-center mx-auto mb-4">
              <LinkIcon className="w-6 h-6 text-brinsa-azul" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {statsLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : `${stats.openRate}%`}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Tasa de lectura</div>
          </Card>
        </motion.div>
      ) : (
        <>
          {notificationsLoading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : notifications.length > 0 ? (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                {notifications.map((notification: any, index: number) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow">
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Bell className="w-6 h-6 text-brinsa-azul" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
                                {notification.title}
                              </h3>

                              <p className="text-brinsa-azulOscuro/80 mb-3 line-clamp-2">
                                {notification.body}
                              </p>

                              <div className="flex flex-wrap items-center gap-4 text-sm text-brinsa-azulOscuro/70">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {new Date(notification.createdAt).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </div>

                                {notification.sends && (
                                  <div className="flex items-center">
                                    <Users className="w-4 h-4 mr-1" />
                                    {notification.sends.length} destinatarios
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="relative">
                            <button
                              onClick={() => setActionMenuOpen(
                                actionMenuOpen === notification.id ? null : notification.id
                              )}
                              className="p-2 hover:bg-brinsa-celeste/10 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-brinsa-azul" />
                            </button>

                            {actionMenuOpen === notification.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setActionMenuOpen(null)}
                                />
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-brinsa-celeste/30 py-1 z-20">
                                  <button
                                    onClick={() => handleView(notification)}
                                    className="w-full px-4 py-2 text-left text-sm text-brinsa-azul hover:bg-brinsa-celeste/10 flex items-center"
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Ver detalles
                                  </button>
                                  <button
                                    onClick={() => handleEdit(notification)}
                                    className="w-full px-4 py-2 text-left text-sm text-brinsa-azul hover:bg-brinsa-celeste/10 flex items-center"
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => handleDelete(notification)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Eliminar
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>

              {pagination && pagination.totalPages > 1 && (
                <Card>
                  <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-brinsa-azulOscuro/80">
                      Página {pagination.page} de {pagination.totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setPage(page - 1)} disabled={page === 1}>
                        Anterior
                      </Button>
                      <Button size="sm" onClick={() => setPage(page + 1)} disabled={page === pagination.totalPages}>
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
            </>
          ) : (
            <Card className="p-12">
              <div className="text-center">
                <Bell className="w-16 h-16 text-brinsa-azul/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-brinsa-azul mb-2">No hay notificaciones</h3>
                <p className="text-brinsa-azulOscuro/80">Las notificaciones enviadas aparecerán aquí</p>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Modal Crear */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Crear Nueva Notificación" size="xl">
        <form onSubmit={handleSubmitCreate(onSendNotification)} className="space-y-6">
          <Alert type="info" title="Información importante" message="Las notificaciones se enviarán tanto como mensajes en la plataforma como emails a los usuarios que tengan email registrado." />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Título *</label>
              <input {...registerCreate('title', { required: 'El título es requerido' })} type="text" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" placeholder="Ej: Nueva actualización disponible" />
              {errorsCreate.title && <p className="mt-1 text-sm text-red-600">{errorsCreate.title.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Imagen (URL opcional)</label>
              <input {...registerCreate('imageUrl')} type="url" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" placeholder="https://ejemplo.com/imagen.jpg" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">Mensaje *</label>
            <textarea {...registerCreate('body', { required: 'El mensaje es requerido' })} rows={4} className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" placeholder="Escribe aquí el contenido de tu notificación..." />
            {errorsCreate.body && <p className="mt-1 text-sm text-red-600">{errorsCreate.body.message}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Texto del botón (opcional)</label>
              <input {...registerCreate('actionButtonText')} type="text" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" placeholder="Ej: Ver más información" />
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">URL del botón (opcional)</label>
              <input {...registerCreate('actionButtonUrl')} type="url" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" placeholder="https://ejemplo.com/accion" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-4">Destinatarios *</label>
            <div className="space-y-3">
              <label className="flex items-center p-3 rounded-lg border border-brinsa-celeste/40 hover:bg-brinsa-celeste/10 transition-colors">
                <input {...registerCreate('recipientType')} type="radio" value="all" className="mr-3 accent-brinsa-azul" />
                <Globe className="w-4 h-4 mr-2 text-brinsa-azul" />
                <span className="text-brinsa-azulOscuro">Todos los usuarios</span>
              </label>

              <label className="flex items-center p-3 rounded-lg border border-brinsa-celeste/40 hover:bg-brinsa-celeste/10 transition-colors">
                <input {...registerCreate('recipientType')} type="radio" value="group" className="mr-3 accent-brinsa-azul" />
                <Users className="w-4 h-4 mr-2 text-brinsa-azul" />
                <span className="text-brinsa-azulOscuro">Grupo específico</span>
              </label>

              <label className="flex items-center p-3 rounded-lg border border-brinsa-celeste/40 hover:bg-brinsa-celeste/10 transition-colors">
                <input {...registerCreate('recipientType')} type="radio" value="users" className="mr-3 accent-brinsa-azul" />
                <User className="w-4 h-4 mr-2 text-brinsa-azul" />
                <span className="text-brinsa-azulOscuro">Usuarios específicos</span>
              </label>
            </div>

            {recipientType === 'group' && (
              <select {...registerCreate('groupId')} className="mt-3 w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul">
                <option value="">Seleccionar grupo...</option>
                <option value="zona-norte">Zona Norte</option>
                <option value="zona-sur">Zona Sur</option>
                <option value="administradores">Administradores</option>
              </select>
            )}

            {recipientType === 'users' && (
              <textarea {...registerCreate('userCedulas')} className="mt-3 w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" rows={3} placeholder="Ingresa las cédulas separadas por comas (ej: 12345678, 87654321, 11223344)" />
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-brinsa-celeste/30">
            <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} disabled={createNotificationMutation.isPending}>Cancelar</Button>
            <Button type="submit" leftIcon={<Send className="w-4 h-4" />} disabled={createNotificationMutation.isPending}>
              {createNotificationMutation.isPending ? 'Enviando...' : 'Enviar Notificación'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Ver */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Detalles de la Notificación" size="lg">
        {selectedNotification && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-1">Título</label>
              <p className="text-brinsa-azulOscuro">{selectedNotification.title}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-1">Mensaje</label>
              <p className="text-brinsa-azulOscuro whitespace-pre-wrap">{selectedNotification.body}</p>
            </div>

            {selectedNotification.imageUrl && (
              <div>
                <label className="block text-sm font-medium text-brinsa-azul mb-1">Imagen</label>
                <img src={selectedNotification.imageUrl} alt="Notificación" className="w-full max-h-64 object-contain rounded-lg border border-brinsa-celeste/30" />
              </div>
            )}

            {selectedNotification.actionButtonText && (
              <div>
                <label className="block text-sm font-medium text-brinsa-azul mb-1">Botón de acción</label>
                <p className="text-brinsa-azulOscuro">
                  {selectedNotification.actionButtonText}
                  {selectedNotification.actionButtonUrl && (
                    <a href={selectedNotification.actionButtonUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-brinsa-azul hover:underline">(Ver enlace)</a>
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brinsa-azul mb-1">Fecha de envío</label>
                <p className="text-brinsa-azulOscuro">{new Date(selectedNotification.createdAt).toLocaleString('es-ES')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brinsa-azul mb-1">Destinatarios</label>
                <p className="text-brinsa-azulOscuro">{selectedNotification.sends?.length || 0} usuarios</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Editar */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedNotification(null); resetEdit(); }} title="Editar Notificación" size="xl">
        <form onSubmit={handleSubmitEdit(onUpdateNotification)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Título *</label>
              <input {...registerEdit('title', { required: 'El título es requerido' })} type="text" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" />
              {errorsEdit.title && <p className="mt-1 text-sm text-red-600">{errorsEdit.title.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Imagen (URL opcional)</label>
              <input {...registerEdit('imageUrl')} type="url" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">Mensaje *</label>
            <textarea {...registerEdit('body', { required: 'El mensaje es requerido' })} rows={4} className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" />
            {errorsEdit.body && <p className="mt-1 text-sm text-red-600">{errorsEdit.body.message}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">Texto del botón (opcional)</label>
              <input {...registerEdit('actionButtonText')} type="text" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" />
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">URL del botón (opcional)</label>
              <input {...registerEdit('actionButtonUrl')} type="url" className="w-full px-3 py-2 border rounded-lg border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul" />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-brinsa-celeste/30">
            <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); setSelectedNotification(null); resetEdit(); }} disabled={updateNotificationMutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateNotificationMutation.isPending}>
              {updateNotificationMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Dialog Confirmar eliminación */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Eliminar Notificación"
        message="¿Estás seguro de que deseas eliminar esta notificación? Esta acción no se puede deshacer y la notificación será eliminada para todos los usuarios."
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={deleteNotificationMutation.isPending}
      />
      <QuickActions />
    </div>
  );
};

export default AdminNotifications;